const EPS=1e-10;
function fieldNumber(v){
  if(typeof v==='number') return Number.isFinite(v)?v:0;
  const s=String(v??'').trim().replace(/\s/g,'');
  if(!s) return 0;
  if(s.includes(',')) return Number(s.replace(/\./g,'').replace(',','.'))||0;
  return Number(s)||0;
}
export const money=v=>new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(Number(v)||0);
export const pct=(v,d=3)=>`${(Number(v)||0).toFixed(d).replace('.',',')}%`;
export const annualEquivalent=m=>(Math.pow(1+(Number(m)||0)/100,12)-1)*100;

export function pricePayment(pv,monthlyPct,n){
  const i=monthlyPct/100;
  if(n<=0)return 0;
  if(Math.abs(i)<EPS)return pv/n;
  return pv*i/(1-Math.pow(1+i,-n));
}
export function priceSchedule(pv,monthlyPct,n,paymentOverride=null){
  const i=monthlyPct/100,pmt=paymentOverride??pricePayment(pv,monthlyPct,n);
  let balance=pv; const rows=[];
  for(let k=1;k<=n;k++){
    const interest=balance*i;
    let payment=Math.min(pmt,balance+interest);
    let amortization=payment-interest;
    if(amortization<0) amortization=0;
    if(k===n || balance-amortization<0.01){amortization=balance;payment=interest+amortization;}
    balance=Math.max(0,balance-amortization);
    rows.push({k,payment,interest,amortization,balance});
    if(balance<=0) break;
  }
  return rows;
}
export function priceScheduleFixedPayment(pv,monthlyPct,payment,maxMonths=1200){
  const i=monthlyPct/100;
  if(!(pv>0)||!(payment>0)) return [];
  if(i>0 && payment<=pv*i) return [];
  let balance=pv; const rows=[];
  for(let k=1;k<=maxMonths && balance>0.005;k++){
    const interest=balance*i;
    let amortization=payment-interest;
    if(amortization<=0) return [];
    let actualPayment=payment;
    if(amortization>balance){amortization=balance;actualPayment=interest+amortization;}
    balance=Math.max(0,balance-amortization);
    rows.push({k,payment:actualPayment,interest,amortization,balance});
  }
  return rows;
}
export function sacSchedule(pv,monthlyPct,n){
  const i=monthlyPct/100,a=n>0?pv/n:0;let balance=pv;const rows=[];
  for(let k=1;k<=n;k++){
    const interest=balance*i,amortization=Math.min(a,balance),payment=interest+amortization;
    balance=Math.max(0,balance-amortization);rows.push({k,payment,interest,amortization,balance});
  }
  return rows;
}
export function sacScheduleFixedAmortization(pv,monthlyPct,amortization,maxMonths=1200){
  const i=monthlyPct/100;
  if(!(pv>0)||!(amortization>0)) return [];
  let balance=pv; const rows=[];
  for(let k=1;k<=maxMonths && balance>0.005;k++){
    const a=Math.min(amortization,balance),interest=balance*i,payment=a+interest;
    balance=Math.max(0,balance-a);rows.push({k,payment,interest,amortization:a,balance});
  }
  return rows;
}
export function impliedSacRateFromFirstPayment(pv,firstPayment,n){
  if(!(pv>0&&firstPayment>0&&n>0)) return null;
  const amortization=pv/n;
  const interest=firstPayment-amortization;
  if(interest<0) return null;
  return (interest/pv)*100;
}
export function impliedMonthlyRate(pv,pmt,n){
  if(!(pv>0&&pmt>0&&n>0)||pmt*n<pv)return null;
  let lo=0,hi=1;
  const f=r=>pv*r/(1-Math.pow(1+r,-n))-pmt;
  while(f(hi)<0&&hi<100)hi*=2;
  for(let j=0;j<180;j++){const mid=(lo+hi)/2;if(f(mid)>0)hi=mid;else lo=mid;}
  return ((lo+hi)/2)*100;
}
export function xirrMonthly(netReceived,cashflows){
  if(!(netReceived>0)||!cashflows.length)return null;
  let lo=0,hi=1;
  const npv=r=>netReceived-cashflows.reduce((s,c)=>s+c.amount/Math.pow(1+r,c.month),0);
  if(npv(0)>0)return null;
  while(npv(hi)<0&&hi<100)hi*=2;
  for(let k=0;k<180;k++){const m=(lo+hi)/2;if(npv(m)>0)hi=m;else lo=m;}
  return ((lo+hi)/2)*100;
}
export function summarize(rows){
  return rows.reduce((a,r)=>({payments:a.payments+r.payment,interest:a.interest+r.interest,amortization:a.amortization+r.amortization}),{payments:0,interest:0,amortization:0});
}
export function compare(contractRate,referenceRate){
  if(!(referenceRate>0)||contractRate==null)return null;
  const pp=contractRate-referenceRate,rel=((contractRate/referenceRate)-1)*100;
  let level='informativo';
  if(rel>=50)level='atenção elevada';else if(rel>=25)level='atenção';else if(rel>=10)level='acompanhar';
  return {pp,rel,level};
}
export function financedExtras(f){
  return ['insuranceUpfront','iof','registration','appraisal','cadastreFee','otherFees']
    .reduce((s,k)=>s+(f[`${k}Financed`]?fieldNumber(f[k]):0),0);
}
export function monthlyExtras(f){
  return fieldNumber(f.insuranceMonthly)+fieldNumber(f.otherMonthly);
}
export function cetFromCashflow({netReceived,rows,monthlyExtra=0}){
  const flows=rows.map(r=>({month:r.k,amount:r.payment+monthlyExtra}));
  const m=xirrMonthly(netReceived,flows);
  return m==null?null:{monthly:m,annual:annualEquivalent(m)};
}
export function extraCostImpact(extraPrincipal,monthlyPct,n,system='price'){
  if(!(extraPrincipal>0)||n<=0)return {principal:0,total:0,financeCost:0};
  const rows=system==='sac'?sacSchedule(extraPrincipal,monthlyPct,n):priceSchedule(extraPrincipal,monthlyPct,n);
  const sum=summarize(rows);
  return {principal:extraPrincipal,total:sum.payments,financeCost:sum.payments-extraPrincipal};
}
export function balanceAfter(rows,paidInstallments){
  const paid=Math.max(0,Math.floor(Number(paidInstallments)||0));
  if(!rows.length) return 0;
  if(paid<=0) return rows[0].payment-rows[0].interest+rows[0].balance; // principal inicial
  if(paid>=rows.length) return 0;
  return rows[paid-1].balance;
}
export function remainingRows(rows,paidInstallments){
  const paid=Math.max(0,Math.floor(Number(paidInstallments)||0));
  return rows.slice(Math.min(paid,rows.length)).map((r,idx)=>({...r,k:idx+1}));
}
export function amortizationScenarios({system,pv,monthlyPct,totalMonths,paidInstallments,extraAmount,contractPayment}){
  const n=Math.max(1,Math.floor(totalMonths||0));
  const paid=Math.max(0,Math.min(n,Math.floor(paidInstallments||0)));
  const baseline=system==='sac'?sacSchedule(pv,monthlyPct,n):priceSchedule(pv,monthlyPct,n,contractPayment||null);
  const currentBalance=paid===0?pv:(paid>=baseline.length?0:baseline[paid-1].balance);
  const baseRemaining=remainingRows(baseline,paid);
  const baseSummary=summarize(baseRemaining);
  const remainingMonths=baseRemaining.length;
  const extra=Math.max(0,Math.min(Number(extraAmount)||0,currentBalance));
  const newBalance=Math.max(0,currentBalance-extra);

  let reduceTerm=[],reducePayment=[];
  if(newBalance>0 && remainingMonths>0){
    if(system==='sac'){
      const originalAmortization=pv/n;
      reduceTerm=sacScheduleFixedAmortization(newBalance,monthlyPct,originalAmortization);
      reducePayment=sacSchedule(newBalance,monthlyPct,remainingMonths);
    }else{
      const pmt=contractPayment||pricePayment(pv,monthlyPct,n);
      reduceTerm=priceScheduleFixedPayment(newBalance,monthlyPct,pmt);
      reducePayment=priceSchedule(newBalance,monthlyPct,remainingMonths);
    }
  }

  const termSummary=summarize(reduceTerm),paymentSummary=summarize(reducePayment);
  return {
    paid,currentBalance,remainingMonths,extra,newBalance,
    baseline:baseRemaining,baselineSummary:baseSummary,
    reduceTerm,reduceTermSummary:termSummary,
    reducePayment,reducePaymentSummary:paymentSummary,
    termInterestSavings:Math.max(0,baseSummary.interest-termSummary.interest),
    paymentInterestSavings:Math.max(0,baseSummary.interest-paymentSummary.interest),
    termMonthsSaved:Math.max(0,remainingMonths-reduceTerm.length),
    newPayment:reducePayment.length?reducePayment[0].payment:0,
    payoffEstimated:currentBalance,
    futureInterestAvoided:baseSummary.interest
  };
}
export function cumulativeSeries(rows){
  let interest=0,amortization=0,payments=0;
  return rows.map(r=>{
    interest+=r.interest; amortization+=r.amortization; payments+=r.payment;
    return {k:r.k,balance:r.balance,interest,amortization,payments,payment:r.payment};
  });
}
export function normalizeRows(rows,targetLength){
  const out=[];
  for(let i=0;i<targetLength;i++){
    const r=rows[i]||rows.at(-1)||{balance:0,interest:0,amortization:0,payment:0};
    out.push({...r,k:i+1});
  }
  return out;
}
export function alternativeScenario({system,pv,months,referenceRate}){
  if(!(pv>0&&months>0&&referenceRate>=0)) return {rows:[],summary:{payments:0,interest:0,amortization:0}};
  const rows=system==='sac'?sacSchedule(pv,referenceRate,months):priceSchedule(pv,referenceRate,months);
  return {rows,summary:summarize(rows)};
}
export function diagnosticScore({contractRate,referenceRate,extrasFinanced,principal,cetMonthly,declaredRate,insuranceFlags=0}){
  let score=0; const reasons=[];
  if(referenceRate>0 && contractRate!=null){
    const rel=(contractRate/referenceRate-1)*100;
    if(rel>=50){score+=45;reasons.push('Taxa informada/reconstruída está muito acima da referência consultada.');}
    else if(rel>=25){score+=30;reasons.push('Taxa está significativamente acima da referência consultada.');}
    else if(rel>=10){score+=15;reasons.push('Taxa está moderadamente acima da referência consultada.');}
  }
  if(principal>0 && extrasFinanced/principal>=0.08){score+=20;reasons.push('Encargos financiados representam parcela relevante do principal.');}
  else if(principal>0 && extrasFinanced/principal>=0.03){score+=10;reasons.push('Há encargos incorporados ao financiamento que merecem leitura separada.');}
  if(cetMonthly!=null && declaredRate>0 && cetMonthly-declaredRate>=0.5){score+=15;reasons.push('CET reconstruído está sensivelmente acima da taxa contratual informada.');}
  if(insuranceFlags>=2){score+=20;reasons.push('Respostas sobre o seguro indicam necessidade de análise documental.');}
  else if(insuranceFlags===1){score+=10;reasons.push('Há ao menos um ponto de atenção na contratação do seguro.');}
  score=Math.min(100,score);
  let level='sem sinal relevante';
  if(score>=60) level='atenção elevada';
  else if(score>=30) level='atenção';
  else if(score>=15) level='acompanhar';
  if(!reasons.length) reasons.push('Os dados informados não geraram um sinal técnico relevante nesta triagem.');
  return {score,level,reasons};
}
